/**
 * The language the interface speaks, and the words it says in it.
 *
 * Not a general translation framework, and deliberately so. It answers one
 * question the reader kept asking of a French library: why is the shelf around
 * my French books written in English? Everything here follows from that.
 *
 * ## Which language
 *
 * The interface speaks whatever the reader reads in. There is already a control
 * for that, in the reader's header, and adding a second one would only invite
 * the two to disagree. So the chain is: the language chosen by hand, else the
 * one the library resolved to, else the browser's, else English.
 *
 * The browser is last on purpose, and the reason is the same one `house_language`
 * gives on the server: a phone's menus are set once, in whatever the shop had,
 * while a shelf of translations is built book by book by the person about to
 * read it. Of the two, the shelf is the better evidence.
 *
 * ## How a string is found
 *
 * A flat catalogue, split into files by screen so several people can work on it
 * at once, and typed so that a French entry cannot be forgotten: `fr` is
 * declared as covering every key of `en`, which makes a missing translation a
 * compile error rather than a word that silently shows up in English.
 */

import { useSyncExternalStore } from "react";
import { onReadingLangChange, readingLang, uiLangFallback } from "../lib/lang";
import { messages } from "./messages";

export type UiLang = "en" | "fr";
export type Key = keyof (typeof messages)["en"];

const SUPPORTED: UiLang[] = ["en", "fr"];

function supported(code: string | null | undefined): UiLang | null {
  const short = (code ?? "").slice(0, 2).toLowerCase() as UiLang;
  return SUPPORTED.includes(short) ? short : null;
}

/** The language to write the interface in, right now. */
export function uiLang(): UiLang {
  const chosen = readingLang();
  return (
    (chosen !== "auto" ? supported(chosen) : null) ??
    supported(uiLangFallback()) ??
    supported(navigator.language) ??
    "en"
  );
}

/**
 * One string, in the current language, with `{placeholders}` filled in.
 *
 * A missing key returns the key itself rather than throwing. Typing makes that
 * unreachable from this codebase; it stays because a blank interface is a worse
 * way to learn about a typo than a visible one.
 */
export function t(key: Key, vars?: Record<string, string | number>): string {
  const table = messages[uiLang()] as Record<string, string>;
  const raw = table[key] ?? (messages.en as Record<string, string>)[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * The plural of a counted thing.
 *
 * French and English agree on the only rule this interface needs: one is
 * singular, everything else is not, zero included. Written out rather than
 * reached for through `Intl.PluralRules` because a catalogue with two forms per
 * key cannot express what that would give us, and pretending otherwise would
 * make the next language's translator's job harder, not easier.
 */
export function plural(count: number, one: Key, many: Key, vars?: Record<string, string | number>) {
  return t(count === 1 ? one : many, { count, ...vars });
}

/**
 * Re-render this component when the language changes.
 *
 * The choice lives outside React, in `localStorage`, because it is read by
 * plain functions such as `t` that no component owns. This is the bridge back.
 */
export function useUiLang(): UiLang {
  return useSyncExternalStore(onReadingLangChange, uiLang, () => "en" as UiLang);
}

/**
 * The name of a language, written in the language of the interface.
 *
 * `Intl.DisplayNames` knows every code and every language's word for it, which
 * a hand written table cannot: the table this replaced held eleven languages,
 * named in English, and would have shown "German" to a French reader. The
 * fallback is the code upper cased, for the browser too old to have the API and
 * for a code that is not a language at all.
 */
export function langLabel(code: string): string {
  let name: string;
  try {
    name = new Intl.DisplayNames([uiLang()], { type: "language" }).of(code) ?? code;
  } catch {
    return code.toUpperCase();
  }
  // French writes its language names in lower case and is right to, but these
  // are labels on chips sitting beside `Influence` and `Musique`, where a lone
  // lower case word reads as a mistake rather than as orthography. Capitalised
  // as a label, not as a word.
  return name.charAt(0).toLocaleUpperCase(uiLang()) + name.slice(1);
}

/**
 * Keep `<html lang>` and the tab's title in step with the interface.
 *
 * Not decoration. `lang` is what tells a screen reader which voice to read the
 * page in, and a French interface announced as English is read out with English
 * phonemes, which is worse than useless. It is also what a browser's translate
 * offer keys off, so getting it wrong invites the browser to translate a page
 * that is already in the right language.
 *
 * Returns its own unsubscribe so a caller can stop it; nothing does today,
 * because the only caller is the entry point and it lives as long as the tab.
 */
export function trackDocumentLang(): () => void {
  const apply = () => {
    document.documentElement.lang = uiLang();
  };
  apply();
  return onReadingLangChange(apply);
}
